import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { APPLICATIONS } from '../data/products';
import { Reveal } from '../components/Reveal';

const GROUPS = [
  { id: 'hospitality', title: 'Hospitality', to: '/hospitality',
    desc: 'Signature suites for luxury resorts, boutique hotels, glamping and premium short-stay rentals.' },
  { id: 'residential', title: 'Residential', to: '/residential',
    desc: 'Modern residences delivered as products — from weekend escapes to full farmhouse retreats.' },
  { id: 'commercial',  title: 'Commercial Spaces', to: '/commercial',
    desc: 'Design-led retail, pop-up and modular office spaces installed in days, not months.' },
];

export default function Applications() {
  return (
    <main data-testid="applications-page" className="bg-white text-[#111]">
      <Helmet>
        <title>Applications | MODULAROOM</title>
        <meta name="description" content="MODULAROOM units power luxury resorts, glamping cabins, weekend homes, farmhouses, retail pop-ups and modular offices. Factory-built modular hospitality and off-site construction, delivered turnkey." />
        <link rel="canonical" href="https://modularoom.com/applications" />
      </Helmet>
      <section className="pt-40 md:pt-52 pb-16 max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="text-[11px] tracking-[0.32em] uppercase text-black/50">Applications</div>
        <h1 className="font-display text-5xl sm:text-6xl md:text-7xl mt-5 tracking-tight leading-[0.98] max-w-4xl">
          Designed for every purpose.<br/><span className="text-black/40">Endless possibilities.</span>
        </h1>
        <p className="mt-8 max-w-2xl text-lg text-black/65 leading-relaxed">
          From serene getaways to smart workspaces, MODULAROOM adapts to your vision. One product family. Countless environments.
        </p>
      </section>

      {GROUPS.map((g, gi) => {
        const items = APPLICATIONS.filter((a) => a.category === g.id);
        return (
          <section key={g.id} className={`${gi % 2 === 1 ? 'bg-[#F5F5F5]' : 'bg-white'} py-24 md:py-32`}>
            <div className="max-w-[1440px] mx-auto px-6 md:px-12">
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
                <div>
                  <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">0{gi+1} · {g.title}</div>
                  <h2 className="mt-4 font-display text-4xl md:text-5xl tracking-tight leading-[1.02]">{g.title}.</h2>
                  <p className="mt-4 max-w-xl text-[15px] text-black/65 leading-relaxed">{g.desc}</p>
                </div>
                <Link to={g.to} className="text-[12px] tracking-[0.22em] uppercase hover:text-[#C7A36A] transition-colors self-start md:self-end">Explore {g.title} →</Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {items.map((a, i) => (
                  <Reveal key={a.id} delay={i*0.04}>
                    <div data-testid={`app-${a.id}`} className="group">
                      <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-[#F5F5F5]">
                        <img src={a.image} alt={a.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.045]"/>
                        <div className="absolute top-4 left-4 text-[11px] tracking-[0.28em] uppercase text-white/90">/{String(i+1).padStart(2,'0')}</div>
                      </div>
                      <div className="mt-5 font-display text-2xl tracking-tight">{a.title}</div>
                      <div className="mt-2 text-sm text-black/60 leading-relaxed">{a.caption}</div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        );
      })}
    </main>
  );
}
